// ./src/components/features/CreateEventWizard/steps/InvitesStep.tsx
//
// "Who's invited?" — the wizard's guest list (ROADMAP A2b #62 P6c). Shown for every mode people are INVITED to — an
// invite list and a save-the-date (`wizardAsksGuestList`; not an open RSVP — owner, 2026-09-30) — straight after Questions.
//
// Guests are REAL rows the moment they are added, through the Invitations page's own add drawer
// (`PasteRecipientsModal`) with the address book switched on: paste emails or phone numbers, or pick contacts; give each
// invitation a GROUP SIZE and, from 2 up, a GROUP NAME ("John & Anna"). One import call — the server sets up the
// household (P2b). Edit opens the Guests page's own `GuestDrawer`; remove is the Guests page's remove.
//
// ⚠ NOTHING IS SENT HERE. Invitations go out from Communications › Invitations, which the finish screen points at —
//   the host sees the message before it goes, and a wizard step that emails people on Next would be a surprise.
// ⚠ The list's failure is its READ's own result: a failed read shows the error, never "nobody yet" (which reads as
//   "the guests you just added are gone").

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Stack, Text } from '@mantine/core';
import { OpResult } from '@sdflc/api-helpers';
import { useTranslation } from 'react-i18next';
import { formatPhone } from '@em/utils';

import { useDefaultPhoneCountry } from '@/contexts';
import { useToast } from '@/hooks';
import { useDrawerMount } from '@/hooks/useDrawerMount';
import {
  EVENT_GUEST_ROLE,
  EventGuest,
  EventGuestInvitee,
  EventGuestInviteeService,
  EventGuestService,
  EventGuestStats,
  BULK_JOB_KIND,
  BulkJob,
  RsvpGroup,
  RsvpGroupService,
  getGuestDisplayName,
  getGuestEmail,
  getGuestPhone,
} from '@/utils/api';
import { ConfirmModal, LoadMoreButton } from '@/components/generic';
import { GuestDrawer } from '@/pages/private/EventGuestsPage/GuestDrawer';
import { GUESTS_SCOPE } from '@/pages/private/EventGuestsPage/guestScope';
import { PasteRecipientsModal } from '@/pages/private/EventInvitationsPage/PasteRecipientsModal';
import { GuestImportStatus } from '@/components/features/EventGuests/GuestImportStatus';
import { useBulkJobs } from '@/hooks/useBulkJobs';

import { useEventWizard } from '../WizardContext';
import { WizardItemRow } from '../components/WizardItemRow';
import { WizardListSection } from '../components/WizardListSection';
import { EVENT_MODE, EventMode } from '../wizardTypes';
import { useRefreshOnWriteTimeout } from '@/hooks';
import { refusalReason } from '@/utils';

const PAGE_SIZE = 50;

/** One page of the guest list, with the names of the groups on it. */
interface GuestPage {
  rows: EventGuest[];
  groupNames: Record<string, string>;
  /** #62 P6c Phase B — each invitation's OTHER adults (their own links to it), by guest id. */
  linked: Record<string, EventGuestInvitee[]>;
}

/**
 * #62 P6c Phase B — the adults linked to these invitations. ⓘ FAIL-SOFT, unlike the group names: a missing "also sent to"
 * line hides an extra, it does not misname an invitation — and on a gateway not yet recomposed this read is refused, which
 * must not take the whole guest list down with it.
 */
const readLinkedAdults = async (eventId: string, rows: EventGuest[]): Promise<Record<string, EventGuestInvitee[]>> => {
  const ids = rows.map((g) => g.id).filter((id): id is string => !!id);

  if (ids.length === 0) {
    return {};
  }

  const res = await EventGuestInviteeService.listForGuests(eventId, ids);
  const out: Record<string, EventGuestInvitee[]> = {};

  for (const link of (res.didSucceed() && Array.isArray(res.data) ? res.data : []) as EventGuestInvitee[]) {
    if (link.eventGuestId) {
      out[link.eventGuestId] = [...(out[link.eventGuestId] ?? []), link];
    }
  }

  return out;
};

/**
 * Read one page — the guests AND the names of their groups. ⚠ Either failing fails the page: a guest shown without
 * its group's name would read as a different invitation ("anna@…" where the host typed "John & Anna").
 * ⓘ The Guests page's own list (`GUESTS_SCOPE`): everyone who holds a place, `is_attendee`, newest first.
 */
const readGuestPage = async (eventId: string, page: number): Promise<GuestPage | OpResult> => {
  const res = await EventGuestService.list({ eventId: [eventId], isAttendee: true }, { page, pageSize: PAGE_SIZE });

  if (!res.didSucceed()) {
    return res;
  }

  const rows = (Array.isArray(res.data) ? res.data : []) as EventGuest[];
  const groupIds = [...new Set(rows.map((g) => g.rsvpGroupId).filter((id): id is string => !!id))];
  const linked = await readLinkedAdults(eventId, rows);

  if (groupIds.length === 0) {
    return { rows, groupNames: {}, linked };
  }

  const groups = await RsvpGroupService.listByIds(eventId, groupIds);

  if (!groups.didSucceed()) {
    return groups;
  }

  const groupNames: Record<string, string> = {};

  for (const group of (Array.isArray(groups.data) ? groups.data : []) as RsvpGroup[]) {
    if (group.id && group.name?.trim()) {
      groupNames[group.id] = group.name.trim();
    }
  }

  return { rows, groupNames, linked };
};

const introFor = (mode: EventMode | null): { key: string; text: string } => {
  if (mode === EVENT_MODE.MANAGED_INVITES) {
    return {
      key: 'events:wizard.invites.introInvite',
      text: 'Only the people on this list can reply — each gets their own invitation link. Paste emails or phone numbers, or pick people from your contacts, and say how many each invitation covers: 1 for one person, 2 for a couple, 5 for a family.',
    };
  }

  if (mode === EVENT_MODE.SAVE_THE_DATE) {
    return {
      key: 'events:wizard.invites.introStd',
      text: 'Add the people you will send the save-the-date to — paste emails or phone numbers, or pick people from your contacts, and say how many each one covers.',
    };
  }

  return {
    key: 'events:wizard.invites.introRsvp',
    text: 'Anyone with the event link can reply, and you can also send personal invitations. Add the people you want to invite — paste emails or phone numbers, or pick people from your contacts, and say how many each invitation covers.',
  };
};

interface InvitesStepBodyProps {
  eventId: string;
  mode: EventMode | null;
  /**
   * The EVENT's own "people per reply" (the Guests step's plus-ones). ⚠ A guest's own size OVERRIDES it on the reply,
   * so every new invitation starts here — never at 1, which would quietly switch the plus-ones off for everyone.
   */
  eventGroupSize: number;
}

/**
 * What happens to the list next — ⚠ each sentence TRUE for its mode. A reply to a DRAFT is refused ("not accepting
 * RSVPs"), so an invitation is sent after publishing; a save-the-date takes no replies at all.
 */
const notSentFor = (mode: EventMode | null): { key: string; text: string } => {
  if (mode === EVENT_MODE.SAVE_THE_DATE) {
    return {
      key: 'events:wizard.invites.notSentStd',
      text: 'Nothing is sent from here. A save-the-date takes no replies — once you open RSVPs, send these people their invitations from Communications › Invitations.',
    };
  }

  return {
    key: 'events:wizard.invites.notSent',
    text: 'Nothing is sent yet. When you finish, publish the event — invitation links open the reply form only once it is live — then send the invitations, after seeing the message.',
  };
};

const InvitesStepBody: React.FC<InvitesStepBodyProps> = ({ eventId, mode, eventGroupSize }) => {
  const { t } = useTranslation(['events', 'common']);
  const toast = useToast();
  const { saving } = useEventWizard();
  const defaultPhoneCountry = useDefaultPhoneCountry();

  const [guests, setGuests] = useState<EventGuest[]>([]);
  const [groupNames, setGroupNames] = useState<Record<string, string>>({});
  const [linked, setLinked] = useState<Record<string, EventGuestInvitee[]>>({});
  const [total, setTotal] = useState<number | null>(null);
  // What the list is expected to bring (server rule `expectedHeadsOf`). ⓘ A server that does not know the field yet
  // (gateway not recomposed) REFUSES the read, and the step shows that error — it is never silently left out.
  const [expected, setExpected] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const [isLoaded, setIsLoaded] = useState(false);
  const [readError, setReadError] = useState<OpResult | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  // A re-read of page 1 is in flight — Load more waits for it, or it would append page N+1 to a list reset to page 1.
  const [reloading, setReloading] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState<EventGuest | null>(null);
  // Mounted while editing (and while it slides out), opened one commit after it mounted — so it slides in.
  const editDrawer = useDrawerMount(editing);
  const [pendingRemove, setPendingRemove] = useState<EventGuest | null>(null);
  const [removing, setRemoving] = useState(false);
  // ⚠ A later read supersedes an earlier one — an import's reload must not be overwritten by a slower first read.
  const callRef = useRef(0);

  /** The first page, and how many are on the list. */
  const load = useCallback(async () => {
    const call = ++callRef.current;

    setReloading(true);
    // A reload owns the list: a Load more it supersedes is dropped, and so is its spinner (#114).
    setLoadingMore(false);

    const [pageRes, statsRes] = await Promise.all([readGuestPage(eventId, 1), EventGuestService.listSize(eventId)]);

    if (call !== callRef.current) {
      return;
    }

    setReloading(false);

    // ⚠ Settled on failure too, or a failed read would hold the skeleton for ever.
    setIsLoaded(true);

    if (pageRes instanceof OpResult) {
      setReadError(pageRes);

      return;
    }

    if (!statsRes.didSucceed()) {
      setReadError(statsRes);

      return;
    }

    setReadError(null);
    setGuests(pageRes.rows);
    setGroupNames(pageRes.groupNames);
    setLinked(pageRes.linked);
    setPage(1);
    const stats = statsRes.getDataFirst() as EventGuestStats | undefined;

    setTotal(stats?.totalGuests ?? null);
    setExpected(typeof stats?.expectedHeads === 'number' ? stats.expectedHeads : null);
  }, [eventId]);

  // ROADMAP #131 — a save that timed out may still have landed: re-read what is shown.
  useRefreshOnWriteTimeout(load, isLoaded);

  useEffect(() => {
    void load();
  }, [load]);

  const loadMore = useCallback(async () => {
    // ⚠ Not while the list reloads or a page is already coming: the captured call id would be the reload's, so a page
    // N + 1 of the NEW read would land as current and skip pages 2…N (#119 review).
    if (reloading || loadingMore) {
      return;
    }

    const call = callRef.current;
    const next = page + 1;

    setLoadingMore(true);

    const res = await readGuestPage(eventId, next);

    if (call !== callRef.current) {
      return;
    }

    setLoadingMore(false);

    if (res instanceof OpResult) {
      toast.error(refusalReason(res));

      return;
    }

    setGuests((prev) => [...prev, ...res.rows.filter((g) => !prev.some((p) => p.id === g.id))]);
    setGroupNames((prev) => ({ ...prev, ...res.groupNames }));
    setLinked((prev) => ({ ...prev, ...res.linked }));
    setPage(next);
  }, [eventId, page, toast, reloading, loadingMore]);

  // ⚠ `total` is the list's own count (stats.totalGuests is the same `is_attendee` rows) — never a page-full guess.
  const hasMore = total != null && guests.length < total;
  const existingPersonIds = useMemo(() => guests.map((g) => g.personId).filter((id): id is string => !!id), [guests]);
  // The picker shows a family's linked adults as already on the list too — they already get its invitation.
  const pickerExcludedIds = useMemo(
    () => [...existingPersonIds, ...Object.values(linked).flatMap((ls) => ls.map((l) => l.personId).filter((id): id is string => !!id))],
    [existingPersonIds, linked],
  );
  const existingGuests = useMemo(
    () => ({
      personIds: existingPersonIds,
      emails: guests.map(getGuestEmail).filter((e): e is string => !!e),
      phones: guests.map(getGuestPhone).filter((p): p is string => !!p),
    }),
    [guests, existingPersonIds],
  );

  // #132 — the list is added on the server, in the background: the step shows the import's progress and report, and
  // re-reads its list when an import it saw running finishes.
  const guestImports = useBulkJobs(BULK_JOB_KIND.GUEST_IMPORT, eventId ?? null, () => void load());

  const afterStart = (job: BulkJob) => {
    setAddOpen(false);
    guestImports.track(job);
  };

  const afterEdit = () => {
    setEditing(null);
    void load();
  };

  const confirmRemove = async () => {
    if (!pendingRemove?.id) {
      return;
    }

    setRemoving(true);

    // ⚠ The Guests page's own rule (`applyRemoval`): someone who is ALSO crew only leaves the guest list — deleting the
    // row would take them off the crew too. A plain guest is removed from the event.
    const isCrew = !!pendingRemove.guestRole && pendingRemove.guestRole !== EVENT_GUEST_ROLE.GUEST;
    const res = isCrew
      ? await EventGuestService.update({ id: pendingRemove.id }, { isAttendee: false })
      : await EventGuestService.remove(pendingRemove.id);

    setRemoving(false);

    if (!res.didSucceed()) {
      toast.error(refusalReason(res));

      return;
    }

    setPendingRemove(null);
    void load();
  };

  const titleOf = (g: EventGuest): string =>
    (g.rsvpGroupId ? groupNames[g.rsvpGroupId] : null) || getGuestDisplayName(g);

  const subtitleOf = (g: EventGuest): string => {
    // A guest with no size of their own replies under the event's (null = "the event default", server-side).
    const size = g.rsvpGroupSize ?? eventGroupSize;
    const phone = getGuestPhone(g);
    const contact = getGuestEmail(g) || (phone ? formatPhone(phone) : null);
    const parts = [
      size > 1
        ? t('events:wizard.invites.groupOf', { defaultValue: 'Up to {{count}} people', count: size })
        : t('events:wizard.invites.justOne', { defaultValue: 'Just them' }),
      contact && contact !== titleOf(g) ? contact : null,
      // #62 P6c Phase B — the family's other adults, each with their own link to this one invitation.
      (linked[g.id ?? ''] ?? []).length > 0
        ? t('events:wizard.invites.alsoInvited', {
            defaultValue: 'also invited: {{names}}',
            names: (linked[g.id ?? ''] ?? []).map((l) => [l.person?.firstName, l.person?.lastName].filter(Boolean).join(' ') || l.person?.email || l.person?.phone || '—').join(', '),
          })
        : null,
    ];

    return parts.filter(Boolean).join(' · ');
  };

  const intro = introFor(mode);
  const notSent = notSentFor(mode);

  return (
    <Stack gap="lg">
      <Text size="md" c="dimmed">
        {t(intro.key, { defaultValue: intro.text })}
      </Text>

      <GuestImportStatus imports={guestImports} />

      <WizardListSection
        title={
          total != null && total > 0
            ? expected != null
              ? t('events:wizard.invites.titleExpected', {
                  defaultValue:
                    total === 1
                      ? 'Your guest list · 1 invitation · {{people}} expected'
                      : 'Your guest list · {{count}} invitations · {{people}} expected',
                  count: total,
                  people: t('events:wizard.invites.people', {
                    defaultValue: expected === 1 ? '1 person' : '{{count}} people',
                    count: expected,
                  }),
                })
              : t('events:wizard.invites.titleCount', {
                  defaultValue: total === 1 ? 'Your guest list · 1 invitation' : 'Your guest list · {{count}} invitations',
                  count: total,
                })
            : t('events:wizard.invites.title', { defaultValue: 'Your guest list' })
        }
        addLabel={t('events:wizard.invites.add', { defaultValue: 'Add guests' })}
        onAdd={() => setAddOpen(true)}
        // Adding guests is not plan-gated (the plan decides names vs headcount, not who may be invited).
        gate={null}
        gateWhat={t('events:wizard.invites.another', { defaultValue: 'more guests' })}
        disabled={saving}
        isLoaded={isLoaded}
        readError={readError}
        onRetry={() => void load()}
        errorTitle={t('events:wizard.invites.loadFailed', { defaultValue: 'Could not load the guest list' })}
        operation="eventGuestList"
        emptyText={t('events:wizard.invites.none', { defaultValue: 'Nobody on the list yet.' })}
        isEmpty={guests.length === 0}
      >
        {guests.map((g) => (
          <WizardItemRow
            key={g.id}
            icon={(g.rsvpGroupSize ?? eventGroupSize) > 1 ? 'Users' : 'User'}
            title={titleOf(g)}
            subtitle={subtitleOf(g)}
            disabled={saving}
            onEdit={() => setEditing(g)}
            onRemove={() => setPendingRemove(g)}
          />
        ))}
        <LoadMoreButton
          hasMore={hasMore}
          loading={!isLoaded || reloading}
          loadingMore={loadingMore}
          onLoadMore={() => void loadMore()}
        />
      </WizardListSection>

      <Text size="md" c="dimmed">
        {t(notSent.key, { defaultValue: notSent.text })}
      </Text>

      <PasteRecipientsModal
        opened={addOpen}
        onClose={() => setAddOpen(false)}
        eventId={eventId}
        defaultCountry={defaultPhoneCountry}
        allowContacts
        excludePersonIds={pickerExcludedIds}
        existingGuests={existingGuests}
        defaultGroupSize={eventGroupSize}
        title={t('events:wizard.invites.drawerTitle', { defaultValue: 'Add guests' })}
        submitLabel={(count) => t('events:wizard.invites.addN', { defaultValue: 'Add {{count}}', count })}
        onStarted={afterStart}
      />

      {/* The Guests page's own editor. ⓘ No dates: a date booking belongs on the Guests page, and with the field off
          the drawer never sends one (it cannot un-book anybody from here). */}
      {/* ⚠ Mounted only while editing, keyed by the guest: the drawer reads the event's groups ONCE when it mounts,
          and a drawer mounted with the step would not know the groups the imports just created ("No group"). */}
      {editDrawer.mounted && editDrawer.value ? (
        <GuestDrawer
          key={editDrawer.value.id}
          opened={editDrawer.opened}
          // ⚠ Re-read on close too: the drawer can stop a linked adult's invitation without saving the guest.
          onClose={afterEdit}
          eventId={eventId}
          guest={editDrawer.value}
          defaultRole={GUESTS_SCOPE.defaultRole}
          roleOptions={GUESTS_SCOPE.roleOptions}
          showRole={GUESTS_SCOPE.showRole}
          existingPersonIds={existingPersonIds}
          onSaved={afterEdit}
          recurringDates={false}
          dateSlots={[]}
          dateSlotsLoaded={false}
        />
      ) : null}

      <ConfirmModal
        opened={!!pendingRemove}
        onClose={() => setPendingRemove(null)}
        onConfirm={() => void confirmRemove()}
        type="danger"
        confirmInProgress={removing}
        title={t('events:wizard.invites.removeTitle', { defaultValue: 'Remove from the guest list?' })}
        message={`${t('events:wizard.invites.removeMessage', {
          defaultValue: '“{{name}}” will be taken off this event’s guest list. The contact stays in your address book.',
          name: pendingRemove ? titleOf(pendingRemove) : '',
        })}${
          // #62 P6c Phase B — the family's other adults hold links to THIS invitation; say that they stop working too.
          pendingRemove?.id && (linked[pendingRemove.id] ?? []).length > 0
            ? ` ${t('events:wizard.invites.removeLinked', {
                defaultValue: 'The links sent to {{names}} stop working too.',
                names: (linked[pendingRemove.id] ?? [])
                  .map((l) => [l.person?.firstName, l.person?.lastName].filter(Boolean).join(' ') || l.person?.email || l.person?.phone || '—')
                  .join(', '),
              })}`
            : ''
        }`}
        confirmLabel={t('common:actions.remove', { defaultValue: 'Remove' })}
      />
    </Stack>
  );
};

export const InvitesStep: React.FC = () => {
  const { t } = useTranslation(['events']);
  const { createdEventId, draft } = useEventWizard();

  if (!createdEventId) {
    return (
      <Text size="md" c="dimmed">
        {t('events:wizard.tickets.noEvent', { defaultValue: 'Your event is created at the end of the Guests step.' })}
      </Text>
    );
  }

  return (
    <InvitesStepBody
      eventId={createdEventId}
      mode={draft.eventMode}
      eventGroupSize={Math.max(1, draft.rsvpGroupSize || 1)}
    />
  );
};
